/* ===========================================================================
   El Rose Maison Gallery
   Vanilla JS — handles rail clicks, hero focus + keyboard nav, mobile
   scroll-position → dot sync, modal open/close/nav, variant image swap.

   Public events:
     - listens for 'variant:change' (custom or Dawn's) with detail.variant
     - emits 'erb:ready' once initialised
     - emits 'erb:swap' with detail.index on every image swap

   No framework. No animation libs. Native <dialog>.
   =========================================================================== */
(function () {
  'use strict';

  /** Initialise a single gallery element (idempotent). */
  function init(gallery) {
    if (!gallery || gallery.dataset.erbInited === '1') return;
    gallery.dataset.erbInited = '1';

    var thumbs = Array.from(gallery.querySelectorAll('[data-erb-thumb]'));
    var slides = Array.from(gallery.querySelectorAll('[data-erb-slide]'));
    var hero = gallery.querySelector('[data-erb-hero]');
    var counterCurrent = gallery.querySelector('[data-erb-counter-current]');
    var counterTotal = gallery.querySelector('[data-erb-counter-total]');
    var modal = document.querySelector('[data-erb-modal]');
    var total = slides.length;

    if (total === 0) return;
    if (counterTotal) counterTotal.textContent = String(total).padStart(2, '0');

    function mobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    /** Set the active slide + thumb. On mobile, scroll the hero to match. */
    function activate(index, opts) {
      opts = opts || {};
      if (index < 0) index = total - 1;
      if (index >= total) index = 0;

      thumbs.forEach(function (t, i) {
        var on = i === index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
      });

      slides.forEach(function (s, i) {
        var on = i === index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
      });

      if (counterCurrent) counterCurrent.textContent = String(index + 1).padStart(2, '0');
      gallery.dataset.activeIndex = String(index);

      // On mobile, hero is a scroll container with flex slides — scroll to the active slide.
      if (mobile() && hero && !opts.fromScroll) {
        var slide = slides[index];
        if (slide) {
          var x = slide.offsetLeft;
          hero.scrollTo({ left: x, behavior: opts.instant ? 'auto' : 'smooth' });
        }
      }

      gallery.dispatchEvent(new CustomEvent('erb:swap', { detail: { index: index } }));
    }

    // Thumb click → activate.
    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { activate(i); });
    });

    // Hero focus → keyboard nav + Enter/Space → modal.
    if (hero) {
      hero.addEventListener('click', function () {
        if (mobile()) return;  // mobile uses swipe + modal-by-tap is too easy to mis-fire
        openModal(parseInt(gallery.dataset.activeIndex || '0', 10));
      });
      hero.addEventListener('keydown', function (e) {
        var current = parseInt(gallery.dataset.activeIndex || '0', 10);
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openModal(current);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          activate(current + 1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          activate(current - 1);
        } else if (e.key === 'Home') {
          e.preventDefault();
          activate(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          activate(total - 1);
        }
      });

      // Mobile: scroll-position → active-dot sync (debounced).
      var scrollTimer = null;
      hero.addEventListener('scroll', function () {
        if (!mobile()) return;
        if (scrollTimer) clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var slideWidth = hero.clientWidth;
          if (slideWidth === 0) return;
          var idx = Math.round(hero.scrollLeft / slideWidth);
          if (String(idx) !== gallery.dataset.activeIndex) {
            activate(idx, { fromScroll: true });
          }
        }, 80);
      }, { passive: true });
    }

    // Variant image swap — listen for Dawn's variant change events.
    function handleVariantChange(e) {
      var detail = e.detail || {};
      var variant = detail.variant || detail;
      var featured = variant && (variant.featured_media || variant.featuredMedia);
      var mediaId = featured && (featured.id || featured.media_id);
      if (!mediaId) return;
      var idx = slides.findIndex(function (s) { return String(s.dataset.mediaId) === String(mediaId); });
      if (idx >= 0) activate(idx);
    }
    document.addEventListener('variant:change', handleVariantChange);
    document.addEventListener('on:variant:change', handleVariantChange);

    // Initial activation.
    var initialIdx = slides.findIndex(function (s) { return s.classList.contains('is-active'); });
    activate(initialIdx >= 0 ? initialIdx : 0, { instant: true });

    initModal(modal);
    bindModalToGallery(modal, gallery, slides);

    gallery.dispatchEvent(new CustomEvent('erb:ready'));
  }

  // ---------- Modal (full-viewport zoom) ----------
  function initModal(modal) {
    if (!modal || modal.dataset.erbInited === '1') return;
    modal.dataset.erbInited = '1';

    var stage = modal.querySelector('[data-erb-modal-stage]');
    var counter = modal.querySelector('[data-erb-modal-counter]');

    modal.querySelector('[data-erb-modal-close]').addEventListener('click', function () { modalClose(); });
    modal.querySelector('[data-erb-modal-prev]').addEventListener('click', function () { modalStep(-1); });
    modal.querySelector('[data-erb-modal-next]').addEventListener('click', function () { modalStep(1); });

    modal.addEventListener('click', function (e) {
      if (e.target === modal) modalClose();
    });

    document.addEventListener('keydown', function (e) {
      if (!modal.open) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); modalStep(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); modalStep(1); }
      else if (e.key === 'Home') { e.preventDefault(); modal._index = 0; renderModal(); }
      else if (e.key === 'End') { e.preventDefault(); modal._index = modal._slides.length - 1; renderModal(); }
    });

    modal.addEventListener('close', function () {
      if (modal._gallery) {
        modal._gallery.dispatchEvent(new CustomEvent('erb:sync', { detail: { index: modal._index } }));
      }
    });

    function modalStep(delta) {
      if (!modal._slides) return;
      modal._index = (modal._index + delta + modal._slides.length) % modal._slides.length;
      renderModal();
    }

    function modalClose() {
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    }

    function renderModal() {
      var slide = modal._slides[modal._index];
      if (!slide) return;
      var sourceImg = slide.querySelector('img');
      stage.innerHTML = '';
      if (sourceImg) {
        var fullImg = new Image();
        // Upgrade to widest available — Shopify URLs use ?width=N or &width=N
        var src = sourceImg.currentSrc || sourceImg.src;
        var upgraded = src.replace(/(\?|&)width=\d+/, '$1width=2400');
        fullImg.src = upgraded;
        fullImg.alt = sourceImg.alt || '';
        fullImg.loading = 'eager';
        stage.appendChild(fullImg);
      } else {
        // video / model / external_video — clone source slide content
        var clone = slide.cloneNode(true);
        clone.removeAttribute('aria-hidden');
        clone.classList.remove('erb-gallery__slide');
        stage.appendChild(clone);
      }
      counter.textContent = String(modal._index + 1).padStart(2, '0') + ' / ' + String(modal._slides.length).padStart(2, '0');
    }

    modal._modalStep = modalStep;
    modal._modalClose = modalClose;
    modal._renderModal = renderModal;
  }

  /** Wire a gallery's open-modal action to render its slides into the modal. */
  function bindModalToGallery(modal, gallery, slides) {
    if (!modal) return;
    gallery.addEventListener('erb:sync', function (e) {
      // index activation comes from modal closing — call the gallery's activate
      var thumbs = gallery.querySelectorAll('[data-erb-thumb]');
      if (thumbs[e.detail.index]) thumbs[e.detail.index].click();
    });
  }

  // Open modal for a specific gallery + index.
  function openModal(index) {
    var modal = document.querySelector('[data-erb-modal]');
    var gallery = document.querySelector('[data-erb-gallery]');
    if (!modal || !gallery) return;
    var slides = Array.from(gallery.querySelectorAll('[data-erb-slide]'));
    modal._gallery = gallery;
    modal._slides = slides;
    modal._index = index;
    if (modal._renderModal) modal._renderModal();
    if (typeof modal.showModal === 'function') {
      try { modal.showModal(); }
      catch (e) {
        // Some browsers refuse showModal() if dialog is already open or detached
        modal.setAttribute('open', '');
      }
    } else {
      modal.setAttribute('open', '');
    }
  }

  function boot() {
    document.querySelectorAll('[data-erb-gallery]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
